import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HashRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import TopNavBar from './components/TopNavBar';
import AdminPanel from './components/AdminPanel';
import UserOptions from './components/UserOptions';
import Home from './pages/Home';
import LobbyRoom from './pages/LobbyRoom';
import { apiFetch } from './api';
import ClubEditor from './components/ClubEditor';
import { canAccessAdmin, getEffectivePermissions } from './utils/permissions';
import { applyTheme, getStoredTheme } from './utils/theme';

const ADMIN_SIDEBAR_WIDTH = 250;

function AdminSidebarRouteEffect({ setAdminSidebarOpen }) {
  const location = useLocation();
  const prevPathnameRef = useRef(null);

  useEffect(() => {
    const enteringAdmin = location.pathname === '/admin' && prevPathnameRef.current !== '/admin';
    if (enteringAdmin) {
      setAdminSidebarOpen(true);
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, setAdminSidebarOpen]);

  return null;
}

function MainContent({
  userRole,
  permissions,
  loggedInUser,
  currentUserId,
  sessionReady,
  adminSidebarOpen,
  toggleAdminSidebar,
  onRequireLogin,
  ownedClubId,
}) {
  const location = useLocation();
  const contentMarginLeft = location.pathname === '/admin' && adminSidebarOpen
    ? ADMIN_SIDEBAR_WIDTH
    : 0;

  return (
    <div
      className="main-content"
      style={{ marginLeft: `${contentMarginLeft}px` }}
    >
      <Routes>
        <Route
          path="/"
          element={(
            <Home
              loggedInUser={loggedInUser}
              onRequireLogin={onRequireLogin}
            />
          )}
        />
        <Route
          path="/lobby/:lobbyId"
          element={(
            <LobbyRoom
              loggedInUser={loggedInUser}
              currentUserId={currentUserId}
              sessionReady={sessionReady}
              onRequireLogin={onRequireLogin}
            />
          )}
        />
        <Route
          path="/admin"
          element={
            canAccessAdmin(permissions, userRole) ? (
              <AdminPanel
                adminSidebarOpen={adminSidebarOpen}
                toggleAdminSidebar={toggleAdminSidebar}
                permissions={permissions}
                userRole={userRole}
              />
            ) : (
              <div className="admin-forbidden">403 - Forbidden</div>
            )
          }
        />
        <Route
          path="/settings"
          element={(
            <UserOptions loggedInUser={loggedInUser} />
          )}
        />
        <Route
          path="/club-manage"
          element={
            ownedClubId ? (
              <ClubEditor
                ownerOnly
                fixedClubId={ownedClubId}
                hideClubList
                canAdminAllClubs={false}
              />
            ) : (
              <div className="admin-forbidden">403 - Forbidden</div>
            )
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login');
  const [sessionReady, setSessionReady] = useState(false);
  const [ownedClubId, setOwnedClubId] = useState(null);

  const refreshSession = useCallback(async () => {
    try {
      const response = await apiFetch('/auth/me');
      if (response.ok) {
        const result = await response.json();
        if (result?.ok && result.user?.username) {
          setLoggedInUser(result.user.username);
          setCurrentUserId(result.user.id ?? null);
          setUserRole(result.role || result.user.siteRole || '');
          setPermissions(getEffectivePermissions(result.role, result.permissions));
          if (result.profile?.theme) {
            applyTheme(result.profile.theme);
          } else {
            applyTheme(getStoredTheme());
          }
          try {
            const clubsRes = await apiFetch('/clubs/me');
            const clubsData = await clubsRes.json().catch(() => ({}));
            setOwnedClubId(clubsData?.owned_club?.id ?? null);
          } catch {
            setOwnedClubId(null);
          }
          return;
        }
      }
      setLoggedInUser('');
      setCurrentUserId(null);
      setUserRole('');
      setPermissions([]);
      setOwnedClubId(null);
      applyTheme(getStoredTheme());
    } catch (err) {
      console.error('Error fetching session:', err);
      setLoggedInUser('');
      setCurrentUserId(null);
      setUserRole('');
      setPermissions([]);
      setOwnedClubId(null);
      applyTheme(getStoredTheme());
    } finally {
      setSessionReady(true);
    }
  }, []);

  useEffect(() => {
    applyTheme(getStoredTheme());
    refreshSession();
  }, [refreshSession]);

  const authParamHandledRef = useRef(false);

  useEffect(() => {
    if (!sessionReady || authParamHandledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const wantsSignup = params.get('signup') === '1';
    const wantsLogin = params.get('login') === '1';
    if (!wantsSignup && !wantsLogin) return;

    authParamHandledRef.current = true;

    if (!loggedInUser) {
      if (wantsSignup) {
        setLoginModalMode('signup');
        setLoginModalOpen(true);
      } else if (wantsLogin) {
        setLoginModalMode('login');
        setLoginModalOpen(true);
      }
    }

    if (wantsSignup) params.delete('signup');
    if (wantsLogin) params.delete('login');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [sessionReady, loggedInUser]);

  const toggleAdminSidebar = () => {
    setAdminSidebarOpen((open) => !open);
  };

  const handleLogout = () => {
    setLoggedInUser('');
    setCurrentUserId(null);
    setUserRole('');
    setPermissions([]);
  };

  const handleLoginSuccess = () => {
    refreshSession();
  };

  const handleRequireLogin = () => {
    setLoginModalMode('login');
    setLoginModalOpen(true);
  };

  return (
    <Router>
      <AdminSidebarRouteEffect setAdminSidebarOpen={setAdminSidebarOpen} />
      <TopNavBar
        onAdminLinkClick={toggleAdminSidebar}
        loggedInUser={loggedInUser}
        userRole={userRole}
        permissions={permissions}
        ownedClubId={ownedClubId}
        onLogout={handleLogout}
        onLoginSuccess={handleLoginSuccess}
        loginModalOpen={loginModalOpen}
        loginModalMode={loginModalMode}
        onLoginModalOpenChange={setLoginModalOpen}
      />
      <MainContent
        userRole={userRole}
        permissions={permissions}
        loggedInUser={loggedInUser}
        currentUserId={currentUserId}
        sessionReady={sessionReady}
        adminSidebarOpen={adminSidebarOpen}
        toggleAdminSidebar={toggleAdminSidebar}
        onRequireLogin={handleRequireLogin}
        ownedClubId={ownedClubId}
      />
    </Router>
  );
}

export default App;